import type { MessageKey } from '@dijitalapp/i18n';
import { ERROR_CODES, type ApiErrorBody, type ErrorCode } from '@dijitalapp/shared';

/** Client-side error codes in addition to the server taxonomy. */
/** `E2EE_IDENTITY_CHANGED`: someone we verified changed their encryption identity (client-side check). */
/** `E2EE_SEND_FAILED`: this device could not encrypt the message (keys of a recipient device, own identity…). */
/** `E2EE_SIGNING_KEY_MISSING`: this device does not hold the person's signing keys (another device made them). */
/** `E2EE_DEVICE_UNVERIFIED`: this device is not confirmed by the person yet, so it cannot send encrypted. */
export type ClientErrorCode = ErrorCode | 'NETWORK' | 'E2EE_IDENTITY_CHANGED' | 'E2EE_SEND_FAILED' | 'E2EE_SIGNING_KEY_MISSING' | 'E2EE_DEVICE_UNVERIFIED';

export class ApiError extends Error {
  constructor(
    readonly code: ClientErrorCode,
    readonly status: number,
    readonly fields?: Record<string, string>,
    readonly requestId?: string,
  ) {
    super(code);
  }
}

const KNOWN = new Set<string>(ERROR_CODES);

/** Maps any thrown value to the i18n key of a user-facing message (never raw server text). */
export function errorMessageKey(err: unknown): MessageKey {
  if (err instanceof ApiError) {
    if (err.code === 'NETWORK') return 'error.NETWORK';
    if (err.code === 'E2EE_IDENTITY_CHANGED') return 'error.E2EE_IDENTITY_CHANGED';
    if (err.code === 'E2EE_SEND_FAILED') return 'error.E2EE_SEND_FAILED';
    if (err.code === 'E2EE_SIGNING_KEY_MISSING') return 'error.E2EE_SIGNING_KEY_MISSING';
    if (err.code === 'E2EE_DEVICE_UNVERIFIED') return 'error.E2EE_DEVICE_UNVERIFIED';
    // Nobody is added to a channel: its link is shared instead (an older screen may still try).
    if (err.code === 'FORBIDDEN' && err.fields?.reason === 'channel_add') return 'error.channelAdd';
    if (KNOWN.has(err.code)) return `error.${err.code}` as MessageKey;
  }
  return 'error.UNKNOWN';
}

export function parseErrorBody(status: number, body: unknown): ApiError {
  const e = (body as Partial<ApiErrorBody> | null)?.error;
  const code = e?.code && KNOWN.has(e.code) ? e.code : 'UNKNOWN';
  return new ApiError(code, status, e?.fields, e?.requestId);
}

/** Multipart upload (the browser sets the boundary; Origin is sent automatically for CSRF checks). */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, { method: 'POST', credentials: 'same-origin', body: form });
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  const json: unknown = await res.json().catch(() => null);
  if (!res.ok) throw parseErrorBody(res.status, json);
  return json as T;
}

/**
 * How long a JSON call may take before it is given up (as a NETWORK error): a phone just woken can leave a request
 * hanging for minutes on a dead connection, and the app must try again instead of waiting. Reads get less time
 * than writes (a write may be slow work on the server). Uploads (apiUpload) and file downloads never go through
 * here and have no such limit; `timeoutMs: 0` turns it off for a call that may legitimately run long.
 */
export const DEFAULT_TIMEOUT_MS = { read: 15_000, write: 60_000 } as const;

export interface ApiOptions {
  /** How the browser's HTTP cache may answer (e.g. 'no-cache' when a page must see the server's answer). */
  cache?: RequestCache;
  /** Gives up after this many ms (default DEFAULT_TIMEOUT_MS; 0: never). */
  timeoutMs?: number;
  /** Cancels the call (it then fails as a NETWORK error). */
  signal?: AbortSignal;
}

export async function api<T>(method: string, path: string, body?: unknown, opts: ApiOptions = {}): Promise<T> {
  const timeoutMs = opts.timeoutMs ?? (method === 'GET' || method === 'HEAD' ? DEFAULT_TIMEOUT_MS.read : DEFAULT_TIMEOUT_MS.write);
  const abort = new AbortController();
  const timer = timeoutMs > 0 ? setTimeout(() => abort.abort(), timeoutMs) : null;
  const outer = opts.signal;
  const cancel = () => abort.abort();
  if (outer) {
    if (outer.aborted) abort.abort();
    else outer.addEventListener('abort', cancel, { once: true });
  }
  try {
    let res: Response;
    try {
      res = await fetch(`/api/v1${path}`, {
        method,
        credentials: 'same-origin',
        ...(opts.cache ? { cache: opts.cache } : {}),
        headers: body === undefined ? {} : { 'content-type': 'application/json' },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        signal: abort.signal,
      });
    } catch {
      throw new ApiError('NETWORK', 0);
    }
    if (res.status === 204) return undefined as T;
    // The body is read under the same limit (a response can stall halfway).
    const json: unknown = await res.json().catch(() => null);
    if (abort.signal.aborted) throw new ApiError('NETWORK', 0);
    if (!res.ok) throw parseErrorBody(res.status, json);
    return json as T;
  } finally {
    if (timer) clearTimeout(timer);
    outer?.removeEventListener('abort', cancel);
  }
}
